import {
  LineChart,
  Line,
  CartesianGrid,
  Tooltip,
  XAxis,
  YAxis,
  ResponsiveContainer,
} from "recharts";
import { Wind } from "lucide-react";
import { SectionPanel } from "@/components/ui/section-panel";
import { TagPillRow } from "@/components/ui/tag-pill";
import {
  ZONE_KPI_LABEL,
  ZONE_KPI_NOT_READY_LABEL,
  ZONE_KPI_SCORE_MAX,
  ZONE_KPI_SCORE_MIN,
  type ZoneKpiKind,
} from "@/lib/zone-kpi";
import {
  nowKeyOf,
  type ForecastSplitRow,
  type OperationalLoopPoint,
} from "./operational-loop-data";
import {
  CHART_MARGIN,
  hoverHandlers,
  loopXAxisProps,
  loopYAxis,
  LOOP_STAGE_ACCENT,
  type HoverHandler,
} from "./chart-shared";
import { nowMarker } from "./NowMarker";
import { ForecastTooltip } from "./ForecastTooltip";
import {
  formatDemandTooltipValue,
  type AlignedZoneKpiCurveRow,
} from "./operational-loop-adapter";

export type DemandZoneSeries = {
  zoneId: string;
  zoneName: string;
  /** Shared 15-min loop grid — x-axis, NYT, hover cursor. KPI scores come from `kpiCurve`. */
  points: OperationalLoopPoint[];
  /** Persisted −100…+100 zone-KPI series, already snapped to `points`' step keys.
   *  Undefined for an illustrative-fallback zone; empty when a real zone has no
   *  persisted points yet. A hole is a real not-ready tick — never plotted as 0 %. */
  kpiCurve?: AlignedZoneKpiCurveRow[];
};

export type DemandVisibility = {
  occupancy: boolean;
  co2: boolean;
  temperature: boolean;
};

const ZONE_OPACITY = [1, 0.55, 0.4, 0.28];

const OCCUPANCY_COLOR = "#8b5cf6";
const CO2_COLOR = "#14b8a6";
const TEMP_COLOR = "#f59e0b";

const ZONE_KPI_CURVE_COLOR: Record<ZoneKpiKind, string> = {
  occupancy: OCCUPANCY_COLOR,
  air_quality: CO2_COLOR,
  temperature: TEMP_COLOR,
};

const VISIBLE_KIND: Record<keyof DemandVisibility, ZoneKpiKind> = {
  occupancy: "occupancy",
  co2: "air_quality",
  temperature: "temperature",
};

function kindsToShow(show: DemandVisibility): ZoneKpiKind[] {
  return (Object.keys(show) as (keyof DemandVisibility)[])
    .filter((key) => show[key])
    .map((key) => VISIBLE_KIND[key]);
}

function mergeKpiOntoGrid(zones: DemandZoneSeries[]): ForecastSplitRow[] {
  const length = zones[0]?.points.length ?? 0;
  const curveByZone = new Map(
    zones.map((zone) => [
      zone.zoneId,
      new Map((zone.kpiCurve ?? []).map((row) => [row.key, row])),
    ]),
  );
  const rows: ForecastSplitRow[] = [];
  for (let i = 0; i < length; i++) {
    const base = zones[0].points[i];
    const row: ForecastSplitRow = {
      key: base.key,
      time: base.time,
      isHourMark: base.isHourMark,
    };
    for (const { zoneId } of zones) {
      const kpi = curveByZone.get(zoneId)?.get(base.key);
      for (const kind of ["occupancy", "air_quality", "temperature"] as const) {
        const value = kpi?.[kind] ?? null;
        row[`${kind}Past__${zoneId}`] = base.isPast ? value : null;
        row[`${kind}Future__${zoneId}`] = !base.isPast || base.isNow ? value : null;
      }
    }
    rows.push(row);
  }
  return rows;
}

// 2. Tarve — the zone KPI drivers the recipe actually reacts to, on the shared −100…+100
// scale (docs/spec/knowledge-base.md § Zone KPI scale). Physical ppm / °C / persons stay
// on Signaalit and Sisäilmaolosuhteet; this card is the optimizer's input, not a signal
// strip. `visible` narrows the card to the KPIs the KB's services read for the selected
// equipment (`report.focus`); `null` shows all three.
//
// Optimointi is device-driven — one card for every zone the selected device serves, never
// one card per zone. Shared-unit zones become lines within this chart, distinguished by
// opacity and named in the tooltip.
export function DemandChart({
  zones,
  visible,
  emptyReason,
  unavailable,
  onHover,
}: {
  zones: DemandZoneSeries[];
  visible: DemandVisibility | null;
  emptyReason?: string;
  /** Loading or a source with nothing real yet — shown instead of a demo curve. */
  unavailable?: string;
  onHover?: HoverHandler;
}) {
  const show = visible ?? { occupancy: true, co2: true, temperature: true };
  const anyPoints = zones[0]?.points ?? [];
  const nowKey = nowKeyOf(anyPoints);
  const rows = mergeKpiOntoGrid(zones);
  const firstKey = rows[0]?.key ?? nowKey;
  const multiZone = zones.length > 1;
  const nothing = !show.occupancy && !show.co2 && !show.temperature;
  const kinds = kindsToShow(show);
  const hasKpi = zones.some((z) => (z.kpiCurve?.length ?? 0) > 0);
  const subtitle = kinds.map((kind) => ZONE_KPI_LABEL[kind].toLowerCase()).join(", ");
  // Käyttöaste's future half is a weekly-pattern estimate, never a measurement — the backend's
  // own `pattern_filled` marker, not an inference from `isPast`/`now_index` (task #1884).
  const hasOccupancyPattern =
    kinds.includes("occupancy") &&
    zones.some((z) => z.kpiCurve?.some((row) => row.occupancyPatternFilled));
  // co2/temperature never get a weekly-pattern future (recipe-tainted history — task #1884), so
  // their future side is always a real hole. Named here in words rather than left as a silent
  // gap, using the shared not-ready vocabulary the rest of the app already uses.
  const hasHoleKinds = kinds.some(
    (kind) => kind === "air_quality" || kind === "temperature",
  );

  return (
    <SectionPanel
      icon={Wind}
      title="Sisäiset vaikuttimet & olosuhteet"
      subtitle={
        nothing
          ? "Ei tämän laitteen optimoinnin syöte"
          : `${subtitle.charAt(0).toUpperCase()}${subtitle.slice(1)} (−100…+100 %) — vaikutin: tilan tarve, luetaan ilman reseptiä`
      }
      actions={<TagPillRow names={zones.map((z) => z.zoneName)} />}
      accentColor={LOOP_STAGE_ACCENT.demand}
    >
      {nothing ? (
        <p className="text-xs text-muted-foreground">{emptyReason}</p>
      ) : unavailable ? (
        <p className="text-xs text-muted-foreground">{unavailable}</p>
      ) : !hasKpi ? (
        <p className="text-xs text-muted-foreground">
          Ei persistoitua KPI-historiaa tälle jaksolle
        </p>
      ) : (
        <>
          <div className="h-40 w-full" data-testid="zone-kpi-curve">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={rows}
                margin={CHART_MARGIN}
                syncId="operational-loop"
                {...hoverHandlers(onHover)}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke="var(--border)"
                  strokeOpacity={0.5}
                />
                <XAxis {...loopXAxisProps(rows)} />
                <YAxis
                  {...loopYAxis("left", "kpi", {
                    domain: [ZONE_KPI_SCORE_MIN, ZONE_KPI_SCORE_MAX],
                    ticks: [ZONE_KPI_SCORE_MIN, 0, ZONE_KPI_SCORE_MAX],
                  })}
                />
                <YAxis {...loopYAxis("right", "gutter", { labeled: false })} />
                <Tooltip
                  content={
                    <ForecastTooltip formatValue={formatDemandTooltipValue} />
                  }
                />

                {nowMarker({ x: nowKey, firstX: firstKey, yAxisId: "kpi" })}

                {zones.flatMap((zone, i) => {
                  const opacity = ZONE_OPACITY[i % ZONE_OPACITY.length];
                  const suffix = multiZone ? ` · ${zone.zoneName}` : "";
                  const z = zone.zoneId;
                  const zonePatternFilled = zone.kpiCurve?.some(
                    (row) => row.occupancyPatternFilled,
                  );
                  return kinds.flatMap((kind) => [
                    <Line
                      key={`${kind}-past-${z}`}
                      yAxisId="kpi"
                      type="stepAfter"
                      name={`${ZONE_KPI_LABEL[kind]} (Historia)${suffix}`}
                      dataKey={`${kind}Past__${z}`}
                      stroke={ZONE_KPI_CURVE_COLOR[kind]}
                      strokeOpacity={opacity}
                      strokeWidth={2}
                      dot={false}
                      connectNulls={false}
                    />,
                    <Line
                      key={`${kind}-future-${z}`}
                      yAxisId="kpi"
                      type="stepAfter"
                      name={`${ZONE_KPI_LABEL[kind]} ${
                        kind === "occupancy" && zonePatternFilled
                          ? "(Malliennuste)"
                          : "(Ennuste)"
                      }${suffix}`}
                      dataKey={`${kind}Future__${z}`}
                      stroke={ZONE_KPI_CURVE_COLOR[kind]}
                      strokeOpacity={opacity}
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      dot={false}
                      connectNulls={false}
                    />,
                  ]);
                })}
              </LineChart>
            </ResponsiveContainer>
          </div>
          {hasOccupancyPattern && (
            <p className="mt-2 text-[10px] text-muted-foreground">
              Käyttöasteen ennuste on malliin perustuva arvio samalta
              viikonpäivältä ja kellonajalta — ei mitattu.
            </p>
          )}
          {hasHoleKinds && (
            <p className="mt-2 text-[10px] text-muted-foreground">
              Sisäilman laadun ja lämpötilan tuleva jakso: {ZONE_KPI_NOT_READY_LABEL}
              — ei koskaan mallinnettu tai täytetty asiakaspäässä.
            </p>
          )}
        </>
      )}
    </SectionPanel>
  );
}
